import type { Menu, Reference } from '@ps/web-martech-cms-types';
import classNames from 'clsx';
import React, { useCallback, useState } from 'react';
import { useSelector } from 'react-redux';

import { useCMSReference } from '@swf/cms';
import Arrow from '@swf/core/assets/icons/arrow.svg';
import { select } from '@swf/core/store';

import SubmenuChildren from '../SubmenuChildren/SubmenuChildren';
import SubmenuItem from '../SubmenuItem/SubmenuItem';

import styles from './Submenu.scss';

interface Props {
  menuItemRef: Reference;
  onClick?(): void;
}

const Submenu: React.FC<Props> = ({ menuItemRef, onClick }) => {
  const [openIndex, setOpenIndex] = useState<number | null>(null); // Use null to indicate no menu is open
  const content = useSelector(select.cms.getContent);
  const {
    children,
    page,
    heading,
    url,
    resolver_tag: resolverTag
  } = useCMSReference<Menu>(menuItemRef) as Menu;

  const getSubmenuData = (children: Reference[]) =>
    children.map((item) => content[item.uid] as Menu).filter((item) => item);

  const openSubmenu = useCallback((index: number) => {
    setOpenIndex((currentIndex) => (currentIndex === index ? null : index));
  }, []);

  return (
    <ul className={styles.submenuContainer}>
      {!!children.length && (
        <li className={styles.submenuItem} onClick={onClick}>
          <SubmenuItem pageRef={page} heading={heading} url={url} resolverTag={resolverTag} />
        </li>
      )}
      {getSubmenuData(children).map(
        ({ heading, children, page, uid, url, resolver_tag: resolverTag }, index) => (
          <li key={uid} className={styles.submenuItem} onClick={() => openSubmenu(index)}>
            <SubmenuItem
              pageRef={page}
              heading={heading}
              url={url}
              resolverTag={resolverTag}
              onClick={() => {
                if (!children.length && onClick) {
                  onClick();
                }
              }}
            />
            {children.length > 0 && (
              <Arrow className={classNames(styles.arrow, { [styles.open]: openIndex === index })} />
            )}
            {openIndex === index && children.length > 0 && (
              <SubmenuChildren
                menuItems={getSubmenuData(children)}
                parentPageRef={page}
                parentUrl={url}
                onClick={onClick}
              />
            )}
          </li>
        )
      )}
    </ul>
  );
};

export default Submenu;
